﻿import { CheckCircle, Copy, Link2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function InviteModal({
  isOpen,
  setIsOpen,
  inviteRole,
  setInviteRole,
  generatedLink,
  setGeneratedLink,
  copied,
  setCopied,
  generateInvite,
  activeCompanyId,
}: {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  inviteRole: string;
  setInviteRole: (val: any) => void;
  generatedLink: string | null;
  setGeneratedLink: (val: string | null) => void;
  copied: boolean;
  setCopied: (val: boolean) => void;
  generateInvite: any;
  activeCompanyId: string | null;
}) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setGeneratedLink(null);
          setCopied(false);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white font-bold gap-2"
        >
          <Link2 size={18} /> Gerar Acesso
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px] bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Convite de Acesso ao Sistema
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <p className="text-sm text-slate-400">
            Crie um link seguro para que o seu funcionário acesse o sistema pelo
            próprio celular. O link expira em 24h.
          </p>

          {!generatedLink ? (
            <>
              <div className="grid gap-2">
                <Label className="text-slate-300">Cargo e Permissões</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as any)}
                >
                  <option value="CAIXA">
                    Caixa (Pode fechar ordens e ver fluxo de caixa)
                  </option>
                  <option value="ATENDENTE">
                    Atendente (Pode criar ordens e clientes)
                  </option>
                  <option value="COLABORADOR">
                    Colaborador (Acesso restrito)
                  </option>
                </select>
              </div>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 font-bold gap-2"
                onClick={() =>
                  generateInvite.mutate({
                    role: inviteRole,
                    company_id: activeCompanyId || undefined,
                  })
                }
                disabled={generateInvite.isPending}
              >
                {generateInvite.isPending ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <Link2 size={16} />
                )}
                Gerar Link de Convite
              </Button>
            </>
          ) : (
            <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 space-y-4">
              <p className="text-emerald-400 text-sm font-semibold flex items-center gap-2">
                <CheckCircle size={16} /> Convite Gerado com Sucesso!
              </p>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={generatedLink || ""}
                  className="bg-slate-900 border-slate-700 text-slate-300"
                />
                <Button
                  size="icon"
                  variant={copied ? "default" : "outline"}
                  className={
                    copied
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "border-slate-700 text-slate-300"
                  }
                  onClick={() => {
                    navigator.clipboard.writeText(generatedLink);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                </Button>
              </div>
              <Button
                variant="link"
                className="w-full text-blue-400"
                onClick={() =>
                  window.open(
                    `https://wa.me/?text=Aqui está o seu link de acesso exclusivo ao sistema do Lava Jato: ${generatedLink}`,
                    "_blank",
                  )
                }
              >
                Enviar direto no WhatsApp
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
